import { assertNever, panic } from "@glideapps/ts-necessities";
import type { NonUndefined } from "utility-types";
import { AppKind } from "../common/components/SerializedApp";
import type { TableName } from "../common/database-strings";
import type {
    ClassScreenDescription,
    ComponentDescription,
    PropertyDescription,
    ScreenDescription,
    TableGlideType,
} from "../common/description";
import {
    ActionKind,
    ArrayScreenFormat,
    getTableName,
    makeEmptyComponentDescription,
    makeTableRef,
    ScreenDescriptionKind,
    sheetNameForTable,
} from "../common/description";
import type {
    CardCollectionComponentDescription,
    HeroSectionDescription,
} from "../fluent-components/fluent-components";
import { WireComponentKind } from "../wire/component-kind";
import { CardStyle, UISize } from "../wire/types";
import { getDefaultArrayScreenHandler } from "./array-screens";
import type { AppDescriptionContext } from "./components";
import { getAppKindFromAppDescriptionContext } from "./components";
import { ComponentKindInlineList } from "./components/inline-list";
import {
    makeActionProperty,
    makeEnumProperty,
    makeStringProperty,
    makeSwitchProperty,
    makeTableProperty,
} from "./components/property-utils";
import { summaryColumnsForTable } from "./description-utils";
import { makeArrayScreenDescription, makeClassScreenDescription, makeComponentsForTable } from "./make-screen";

type Summary = NonUndefined<ReturnType<typeof summaryColumnsForTable>>;

function makeSummary(table: TableGlideType): Summary | undefined {
    return summaryColumnsForTable(table, false, true, false, false);
}

function makeBreadcrumbs(): ComponentDescription {
    return makeEmptyComponentDescription(WireComponentKind.Breadcrumbs);
}

function makeHero(summary: Summary): HeroSectionDescription {
    return {
        ...makeEmptyComponentDescription(WireComponentKind.Hero),
        title: summary.titleProperty,
        subtitle: summary.subtitleProperty,
        image: summary.imageURLProperty,
    };
}

function makeClassScreen(
    table: TableGlideType,
    fetchesData: boolean,
    components: readonly ComponentDescription[],
    isDetails: boolean,
    withTitle: boolean
): ClassScreenDescription {
    let title: PropertyDescription | undefined;
    if (withTitle) {
        if (isDetails) {
            const summary = makeSummary(table);
            title = summary?.titleProperty;
        } else {
            title = makeStringProperty(sheetNameForTable(table));
        }
    }
    return {
        kind: ScreenDescriptionKind.Class,
        type: makeTableRef(table),
        canEdit: false,
        canEditFilters: [],
        canDelete: false,
        canDeleteFilters: [],
        searchPlaceholder: undefined,
        isForm: false,
        fetchesData,
        title,
        components,
        transforms: [],
    };
}

function makeDefaultClassComponentsFromSummary(
    summary: Summary,
    omitNavigation: boolean
): readonly ComponentDescription[] {
    const components: ComponentDescription[] = [];
    if (!omitNavigation) {
        components.push(makeBreadcrumbs());
    }
    components.push(makeHero(summary));
    return components;
}

export function makeDefaultClassComponentsForPage(
    table: TableGlideType,
    omitNavigation: boolean
): readonly ComponentDescription[] {
    const summary = makeSummary(table);
    return summary !== undefined ? makeDefaultClassComponentsFromSummary(summary, omitNavigation) : [];
}

export function makeDefaultClassComponents(
    ccc: AppDescriptionContext,
    table: TableGlideType,
    omitNavigation: boolean
): readonly ComponentDescription[] {
    const appKind = getAppKindFromAppDescriptionContext(ccc);
    switch (appKind) {
        case AppKind.App:
            return makeComponentsForTable(ccc, table);
        case AppKind.Page:
            return makeDefaultClassComponentsForPage(table, omitNavigation);
        default:
            return assertNever(appKind);
    }
}

function makeDefaultArrayComponentsFromSummary(
    tableName: TableName,
    summary: Summary,
    isRootScreen: boolean
): readonly ComponentDescription[] {
    const cardCollection: CardCollectionComponentDescription = {
        ...makeEmptyComponentDescription(ComponentKindInlineList),
        cardStyle: makeEnumProperty(CardStyle.Card),
        size: makeEnumProperty(UISize.Medium),
        propertyName: makeTableProperty(tableName),
        format: makeEnumProperty(ArrayScreenFormat.CardCollection),
        title: summary.titleProperty,
        subtitle: summary.subtitleProperty,
        image: summary.imageURLProperty,
        action: makeActionProperty({ kind: ActionKind.PushDetailScreen }),
        caption: undefined,
        allowSearch: makeSwitchProperty(true),
        reverse: undefined,
        limit: undefined,
        groupByColumn: undefined,
        components: undefined,
        transforms: undefined,
        actions: undefined,
        pageSize: undefined,
    };
    if (isRootScreen) {
        return [cardCollection];
    } else {
        return [makeBreadcrumbs(), cardCollection];
    }
}

export function makeDefaultClassScreenForPage(
    table: TableGlideType,
    fetchesData: boolean,
    isRootScreen: boolean
): ClassScreenDescription {
    return makeClassScreen(table, fetchesData, makeDefaultClassComponentsForPage(table, isRootScreen), true, true);
}

export function makeDefaultClassScreen(
    table: TableGlideType,
    ccc: AppDescriptionContext,
    fetchesData: boolean,
    isRootScreen: boolean
): ClassScreenDescription {
    const appKind = getAppKindFromAppDescriptionContext(ccc);
    switch (appKind) {
        case AppKind.App:
            return makeClassScreenDescription(table, ccc, fetchesData);
        case AppKind.Page:
            return makeDefaultClassScreenForPage(table, fetchesData, isRootScreen);
        default:
            return assertNever(appKind);
    }
}

export function makePageScreenComponents(
    table: TableGlideType,
    isRootScreen: boolean
): [components: readonly ComponentDescription[], isDetails: boolean] {
    const summary = makeSummary(table);
    if (summary === undefined) return [[], false];

    if (table.numDataRows === 1) {
        return [makeDefaultClassComponentsFromSummary(summary, isRootScreen), true];
    } else {
        return [makeDefaultArrayComponentsFromSummary(getTableName(table), summary, isRootScreen), false];
    }
}

export function makePageScreen(table: TableGlideType, isRootScreen: boolean): ClassScreenDescription {
    return makeClassScreen(table, true, ...makePageScreenComponents(table, isRootScreen), !isRootScreen);
}

export function makeArrayishScreenDescription(
    table: TableGlideType,
    ccc: AppDescriptionContext,
    isRootScreen: boolean
): ScreenDescription {
    const appKind = getAppKindFromAppDescriptionContext(ccc);
    if (appKind === AppKind.App) {
        return makeArrayScreenDescription(getDefaultArrayScreenHandler(), makeTableRef(table), ccc, true);
    } else if (appKind === AppKind.Page) {
        return makePageScreen(table, isRootScreen);
    } else {
        return panic(appKind);
    }
}
